/**
 * Theme type and palette helpers. Team and compound colours come from the bundle manifest (never
 * hard-coded); each style's own palette lives in its `styles/<id>/theme.ts`.
 */
import type { Manifest, RGB, RGBA, StyleId } from '../api.ts';

export interface Theme {
  id: StyleId;
  /** colour of margins, letterbox and capAspect bars */
  page: RGB;
  /** tab accent */
  accent: RGB;
  /** primary / secondary / faint text */
  text: RGB;
  dim: RGB;
  faint: RGB;
  /** GL clear colour of the map */
  clear: RGBA;
}

export function hexToRgb(hex: string): RGB {
  const h = hex.replace(/^#/, '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (!/^[0-9a-fA-F]{6}$/.test(v)) throw new Error(`bad hex colour '${hex}'`);
  const n = parseInt(v, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(c: RGB): string {
  return `#${((1 << 24) | ((c[0] & 255) << 16) | ((c[1] & 255) << 8) | (c[2] & 255)).toString(16).slice(1).toUpperCase()}`;
}

/** Linear mix a→b by t (0..1), rounded to the nearest integer and clamped. */
export function mix(a: RGB, b: RGB, t: number): RGB {
  const m = (x: number, y: number): number => Math.min(255, Math.max(0, Math.round(x + (y - x) * t)));
  return [m(a[0], b[0]), m(a[1], b[1]), m(a[2], b[2])];
}

/** Multiply brightness (k < 1 darkens). */
export function scaleRgb(c: RGB, k: number): RGB {
  const s = (x: number): number => Math.min(255, Math.max(0, Math.round(x * k)));
  return [s(c[0]), s(c[1]), s(c[2])];
}

/** Rec. 601 luma 0..255 (for choosing dark/light text on a colour plate). */
export function luma(c: RGB): number {
  return 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2];
}

/** Team / driver / compound colours from the manifest, indexed by car index. */
export class Palette {
  private readonly team: RGB[] = [];
  private readonly mate: number[] = [];
  private readonly code: string[] = [];
  private readonly byCode = new Map<string, number>();
  private readonly compound: RGB[] = [];
  private readonly compoundCode: string[];
  private static readonly UNKNOWN: RGB = [128, 128, 128];

  constructor(manifest: Pick<Manifest, 'drivers' | 'teams' | 'compounds'>) {
    const teams = new Map<string, RGB>();
    for (const t of manifest.teams) teams.set(t.team, [t.rgb[0], t.rgb[1], t.rgb[2]]);
    for (const d of manifest.drivers) {
      this.team[d.idx] = teams.get(d.team) ?? [d.rgb[0], d.rgb[1], d.rgb[2]];
      this.mate[d.idx] = d.teammate_rank;
      this.code[d.idx] = d.code;
      this.byCode.set(d.code, d.idx);
    }
    this.compoundCode = manifest.compounds.codes.slice();
    this.compoundCode.forEach((letter, i) => {
      const c = manifest.compounds.rgb[letter];
      this.compound[i] = c ? [c[0], c[1], c[2]] : Palette.UNKNOWN;
    });
  }

  /** team colour of car `idx` */
  teamRgb(idx: number): RGB {
    return this.team[idx] ?? Palette.UNKNOWN;
  }

  /** 0 = first listed driver of the team, 1 = second (gets the teammate marker, 03 §10) */
  teammateRank(idx: number): number {
    return this.mate[idx] ?? 0;
  }

  driverCode(idx: number): string {
    return this.code[idx] ?? '???';
  }

  carIndex(code: string): number {
    return this.byCode.get(code) ?? -1;
  }

  /** colour of a timeline `cmp` value (index into `compounds.codes`) */
  compoundRgb(cmp: number): RGB {
    return this.compound[cmp] ?? Palette.UNKNOWN;
  }

  /** compound letter ('S', 'M', 'H', 'I', 'W', '?') of a timeline `cmp` value */
  compoundLetter(cmp: number): string {
    return this.compoundCode[cmp] ?? '?';
  }
}
